// ============================================================================
// /clock/kiosk — a BUILDING'S time clock on a shared iPad (item "Punch kiosk
// iPad for Research Medical Center's kitchen", approved option B, Ben
// 2026-10-22; walkthrough "Kiosk punch — a walkthrough", 2026-10-22).
//
// The address makes the kiosk: the iPad's web clip opens /clock/kiosk, so a
// kiosk is a kiosk by where it points, not by anything stored that can be lost.
// The database owns the rules (rpc_worker_clock's kiosk path); this screen only
// asks and shows:
//   • not set up  → one sentence, and "Set up this iPad" for a manager. It NEVER
//                   falls back to phone mode.
//   • set up      → mobile number + PIN, the front camera takes one photo, the
//                   punch lands at the iPad's building with no location, a
//                   confirmation, then the screen clears for the next person.
//
// ⛔ Nothing about a worker stays on this iPad: no remembered number or PIN, and
//    the screen clears after every punch and after 45 s idle on any screen.
// ⛔ No office session stays on this iPad either. Setting it up signs a TCS
//    account in, binds the iPad to a building and signs that account out; any
//    office session found on a set-up kiosk is signed out on sight. Frontline
//    workers never reach TCS financial data.
// ⛔ No hours card and no "where are you?" — Ben's decision 1 (22 Oct): the kiosk
//    only punches. The database refuses both to a kiosk as well.
// ⛔ No punch is ever refused for a photo or a location: camera refused or
//    broken, or the upload failed, and the punch still goes through.
// Photos (Ben, 1 Nov 2026): the first time a worker punches on a kiosk she sees the
//    photo notice and answers it (take photo / no photo); she can change her mind on any
//    punch. A worker who chose no photo, or a building whose photos are switched off,
//    punches with phone number and PIN only — no camera, nothing stored, no flag.
// ============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../../shared/components';
import { errorMessage } from '../../shared/errors';
import { useAuth } from '../../shared/auth';
import { SignInScreen } from '../../shared/nav-shell';
import { SmartSelect } from '../../components/smart-select';
import { formatSiteTime } from '../../lib/siteTime';
import {
  kioskStatus,
  registerDevice,
  uploadPunchPhoto,
  workerClock,
  type KioskStatus,
} from './hooks/use-time-data';
import { AutoclosePrompt, HR_HELP_LINE, resolveAutoclose, STR, type AutocloseResolution, type Lang } from './clock';
import type { KioskPhotoMode, PendingAutoclose, PunchResult, WorkerContext } from './types';

/** The company name, as large as the screen allows — this clock is TCS's, not the building's. */
const BRAND = 'text-center text-4xl font-extrabold uppercase leading-tight tracking-tight text-tcs-text sm:text-5xl';

/** Idle on any screen clears it for the next person. */
const IDLE_MS = 45_000;
/** The confirmation stays this long, unless a question about a past night is open. */
const DONE_MS = 8_000;

/** Only what the kiosk needs that /clock does not already say. */
const KSTR: Record<Lang, Record<string, string>> = {
  es: {
    notSetUpTitle: 'Este iPad todavía no está configurado como reloj.',
    notSetUpBody: 'Un gerente lo configura.',
    setUp: 'Configurar este iPad',
    clockHere: 'Reloj de este edificio',
    lookCamera: 'Cámara frontal · mira a la cámara',
    noCamera: 'No se pudo tomar la foto. Marca de todos modos — tu marcada se registra igual.',
    inRecorded: 'ENTRADA registrada',
    outRecorded: 'SALIDA registrada',
    clearsIn: 'Esta pantalla se borra en 8 s',
    done: 'Listo',
    noticeTitle: 'Foto al marcar',
    noticeBody1: 'Cuando marcas entrada o salida en este iPad, toma una foto tuya. La foto solo sirve para confirmar que marcó la persona correcta. No se usa para reconocimiento facial.',
    noticeBody2: 'Solo Recursos Humanos, nómina y los gerentes de TCS de esta zona pueden verla. Se borra sola a los 90 días. Tus horas se guardan para tu pago.',
    takePhoto: 'Entendido — tomar foto',
    noPhoto: 'Prefiero sin foto (mi marcada cuenta igual)',
    noPhotoFromNow: 'Sin foto de ahora en adelante',
    photosAgain: 'Volver a tomar foto',
    pinOnly: 'Sin foto — marcas con tu teléfono y PIN. Tu marcada cuenta igual.',
  },
  en: {
    notSetUpTitle: "This iPad isn't set up as a time clock yet.",
    notSetUpBody: 'A manager sets it up.',
    setUp: 'Set up this iPad',
    clockHere: "This building's time clock",
    lookCamera: 'Front camera · look at the camera',
    noCamera: "The photo couldn't be taken. Punch anyway — your punch still records.",
    inRecorded: 'Punched IN',
    outRecorded: 'Punched OUT',
    clearsIn: 'This screen clears in 8 s',
    done: 'Done',
    noticeTitle: 'Photo when you punch',
    noticeBody1: 'When you punch in or out on this iPad, it takes one photo of you. The photo is only used to confirm the right person punched. It is not used for face recognition.',
    noticeBody2: "Only TCS HR, payroll and the account managers for this area can see it. It deletes itself after 90 days. Your hours are kept for your pay.",
    takePhoto: 'I understand — take photo',
    noPhoto: 'No photo for me (my punch still counts)',
    noPhotoFromNow: 'No photo from now on',
    photosAgain: 'Take photos again',
    pinOnly: 'No photo — you punch with your phone and PIN. Your punch still counts.',
  },
};

interface Done {
  firstName: string;
  eventType: 'in' | 'out';
  at: string;
  siteTz: string | null;
  photo: string | null;
}

/** One frame from the live preview, as a JPEG no wider than 640 px. */
function snap(video: HTMLVideoElement | null): { dataUrl: string; base64: string } | null {
  if (!video || video.readyState < 2 || video.videoWidth === 0) return null;
  const scale = Math.min(1, 640 / video.videoWidth);
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(video.videoWidth * scale);
  canvas.height = Math.round(video.videoHeight * scale);
  const ctx2d = canvas.getContext('2d');
  if (!ctx2d) return null;
  ctx2d.drawImage(video, 0, 0, canvas.width, canvas.height);
  const dataUrl = canvas.toDataURL('image/jpeg', 0.75);
  return { dataUrl, base64: dataUrl.slice(dataUrl.indexOf(',') + 1) };
}

export function KioskClockPage() {
  const { user, signIn, signOut, error: authError, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<KioskStatus | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const [settingUp, setSettingUp] = useState(false);

  const loadStatus = useCallback(async () => {
    setStatusError(null);
    try {
      setStatus(await kioskStatus());
    } catch (e) {
      setStatusError(errorMessage(e, 'Something went wrong — try again.'));
    }
  }, []);
  useEffect(() => { void loadStatus(); }, [loadStatus]);

  // ⛔ No office session may stay on a set-up kiosk.
  useEffect(() => {
    if (status?.kiosk && user && !authLoading) void signOut();
  }, [status?.kiosk, user, authLoading, signOut]);

  if (!status) {
    return (
      <div className="flex min-h-full items-center justify-center p-6 text-sm text-tcs-text-muted">
        {statusError ?? 'Un momento… · One moment…'}
        {statusError && <Button size="sm" className="ml-3" onClick={() => void loadStatus()}>Try again</Button>}
      </div>
    );
  }

  if (!status.kiosk) {
    // Coming back from the Google sign-in redirect lands here signed in: that
    // can only be the manager mid-setup, so the setup form opens by itself.
    if (settingUp || user) {
      return (
        <KioskSetup
          signedIn={!!user}
          authLoading={authLoading}
          authError={authError}
          onSignIn={signIn}
          onCancel={async () => { setSettingUp(false); if (user) await signOut(); }}
          onDone={async () => { await signOut(); setSettingUp(false); await loadStatus(); }}
        />
      );
    }
    return <NotSetUp onSetUp={() => setSettingUp(true)} />;
  }

  return <KioskPunch status={status} />;
}

function NotSetUp({ onSetUp }: { onSetUp: () => void }) {
  return (
    <div className="mx-auto flex min-h-full max-w-xl flex-col px-6 py-8 text-center">
      <div className={BRAND}>Terminal Cleaning Services</div>
      <div className="flex flex-1 flex-col items-center justify-center">
        <div className="text-2xl font-bold text-tcs-text">{KSTR.es.notSetUpTitle}</div>
        <div className="mt-1 text-base text-tcs-text-secondary">{KSTR.es.notSetUpBody}</div>
        <div className="mt-4 text-lg font-semibold text-tcs-text">{KSTR.en.notSetUpTitle}</div>
        <div className="text-sm text-tcs-text-secondary">{KSTR.en.notSetUpBody}</div>
        <Button variant="secondary" className="mt-8" onClick={onSetUp}>
          {KSTR.es.setUp} · {KSTR.en.setUp}
        </Button>
      </div>
      <footer className="pt-8 text-xs text-tcs-text-muted">{STR.es.helpNoNumber}</footer>
    </div>
  );
}

/** A manager binds this iPad to a building, once. Staff words: English. */
function KioskSetup({ signedIn, authLoading, authError, onSignIn, onCancel, onDone }: {
  signedIn: boolean;
  authLoading: boolean;
  authError: string | null;
  onSignIn: () => void;
  onCancel: () => Promise<void>;
  onDone: () => Promise<void>;
}) {
  const [facilityId, setFacilityId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  if (authLoading) return <div className="p-6 text-sm text-tcs-text-muted">Loading session…</div>;
  if (!signedIn) return <SignInScreen onSignIn={onSignIn} error={authError} />;

  async function save() {
    if (!facilityId || name.trim() === '') return;
    setSaving(true); setFailed(null);
    try {
      await registerDevice(name.trim(), 'kiosk', facilityId);
      await onDone();
    } catch (e) {
      setFailed(errorMessage(e, 'This iPad could not be set up — try again.'));
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-full max-w-xl flex-col px-6 py-8">
      <h1 className="text-xl font-bold text-tcs-text">Set up this iPad</h1>
      <p className="mt-1 text-sm text-tcs-text-secondary">
        It becomes the time clock for one building. Anyone with a mobile number and PIN can punch on it; it is never tied to a person.
      </p>
      <label className="mt-5 text-xs font-semibold text-tcs-text-secondary">Which building is this iPad at?</label>
      <div className="mt-1">
        <SmartSelect entity="facility" entityLabel="building" placeholder="Search buildings"
          value={facilityId}
          onChange={(id, row) => {
            setFacilityId(id);
            if (name.trim() === '' && row?.name) setName(`${row.name} — kiosk iPad`);
          }} />
      </div>
      <label className="mt-4 text-xs font-semibold text-tcs-text-secondary" htmlFor="kiosk-name">Name it</label>
      <input id="kiosk-name" value={name} onChange={(e) => setName(e.target.value)}
        className="mt-1 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-3 text-base text-tcs-text" />
      <Button className="mt-5 w-full py-3" loading={saving} disabled={!facilityId || name.trim() === ''} onClick={() => void save()}>
        Make this iPad the building&apos;s time clock
      </Button>
      <p className="mt-2 text-xs text-tcs-text-muted">You are signed out as soon as it is saved. Your office account does not stay on this iPad.</p>
      {failed && <div className="mt-3 rounded-lg border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg">{failed}</div>}
      <button type="button" className="mt-6 text-xs text-tcs-text-muted underline" onClick={() => void onCancel()}>
        Cancel and sign out
      </button>
    </div>
  );
}

function KioskPunch({ status }: { status: KioskStatus }) {
  const [lang, setLang] = useState<Lang>('es');
  const t = { ...STR[lang], ...KSTR[lang] };
  const [phone, setPhone] = useState('');
  const [pin, setPin] = useState('');
  const [ctx, setCtx] = useState<WorkerContext | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [refusal, setRefusal] = useState<string | null>(null);
  const [done, setDone] = useState<Done | null>(null);
  const [pending, setPending] = useState<PendingAutoclose[]>([]);
  const [camera, setCamera] = useState<'off' | 'starting' | 'on' | 'failed'>('off');
  const [photoMode, setPhotoMode] = useState<KioskPhotoMode | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((tr) => tr.stop());
    streamRef.current = null;
    setCamera('off');
  }, []);

  const startCamera = useCallback(async () => {
    setCamera('starting');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => undefined);
      }
      setCamera('on');
    } catch {
      // Refused, missing or busy: the punch still goes through, without a photo.
      setCamera('failed');
    }
  }, []);

  /** Back to a blank screen for the next person. */
  const clearAll = useCallback(() => {
    stopCamera();
    setPhone(''); setPin(''); setCtx(null); setBusy(null); setRefusal(null);
    setDone(null); setPending([]); setLang('es'); setPhotoMode(null);
  }, [stopCamera]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  // ⛔ Idle on ANY screen clears it — a half-typed PIN, a walk-away after
  //    "Hello", an open question. Nothing to clear on a blank screen.
  const idleRef = useRef<number | null>(null);
  const armIdle = useCallback(() => {
    if (idleRef.current) window.clearTimeout(idleRef.current);
    idleRef.current = window.setTimeout(clearAll, IDLE_MS);
  }, [clearAll]);
  const dirty = phone !== '' || pin !== '' || ctx !== null || done !== null;
  useEffect(() => {
    if (!dirty) { if (idleRef.current) window.clearTimeout(idleRef.current); return; }
    armIdle();
    const onActivity = () => armIdle();
    window.addEventListener('pointerdown', onActivity);
    window.addEventListener('keydown', onActivity);
    return () => {
      window.removeEventListener('pointerdown', onActivity);
      window.removeEventListener('keydown', onActivity);
      if (idleRef.current) window.clearTimeout(idleRef.current);
    };
  }, [dirty, armIdle]);

  // The confirmation clears itself — unless a question about a past night is open.
  useEffect(() => {
    if (!done || pending.length > 0) return;
    const id = window.setTimeout(clearAll, DONE_MS);
    return () => window.clearTimeout(id);
  }, [done, pending.length, clearAll]);

  // Keep the preview attached when the video element mounts after the stream.
  useEffect(() => {
    if (videoRef.current && streamRef.current && videoRef.current.srcObject !== streamRef.current) {
      videoRef.current.srcObject = streamRef.current;
      void videoRef.current.play().catch(() => undefined);
    }
  });

  async function enter() {
    setBusy('context'); setRefusal(null);
    try {
      const r = await workerClock('context', phone, pin) as unknown as WorkerContext | { ok: false; message: string };
      if (!r.ok) { setRefusal((r as { message: string }).message); return; }
      const c = r as WorkerContext;
      setCtx(c);
      if (c.language === 'es' || c.language === 'en') setLang(c.language);
      // An older database that sends no photo state keeps the camera as before.
      const mode = c.photo ?? 'on';
      setPhotoMode(mode);
      if (mode === 'on') void startCamera();
    } catch (e) {
      setRefusal(errorMessage(e, 'Something went wrong — try again.'));
    } finally {
      setBusy(null);
    }
  }

  /** Her answer to the notice, or a change of mind on a later punch. */
  async function choosePhoto(choice: 'photo' | 'no_photo') {
    setBusy('photo'); setRefusal(null);
    try {
      const r = await workerClock('photo_choice', phone, pin, { choice }) as { ok?: boolean; message?: string; photo?: KioskPhotoMode };
      if (r.ok === false) { setRefusal(r.message ?? 'Try again.'); return; }
      const mode = r.photo ?? (choice === 'photo' ? 'on' : 'declined');
      setPhotoMode(mode);
      if (mode === 'on') void startCamera(); else stopCamera();
    } catch (e) {
      setRefusal(errorMessage(e, 'Try again.'));
    } finally {
      setBusy(null);
    }
  }

  function switchLang(l: Lang) {
    setLang(l);
    // The language is a fact about the person: once they are in, it sticks to them.
    if (ctx) void workerClock('set_language', phone, pin, { language: l }).catch(() => undefined);
  }

  async function punch(eventType: 'in' | 'out') {
    setBusy(eventType); setRefusal(null);
    try {
      const shot = camera === 'on' ? snap(videoRef.current) : null;
      const photoPath = shot ? await uploadPunchPhoto(phone, pin, shot.base64) : null;
      const r = await workerClock('punch', phone, pin, {
        event_type: eventType,
        photo_path: photoPath,
        user_agent: typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : null,
        tz: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }) as unknown as (PunchResult & { ok?: boolean; message?: string });
      if (r.ok === false) { setRefusal(r.message ?? 'Try again.'); return; }
      stopCamera();
      setDone({
        firstName: ctx?.first_name ?? '',
        eventType,
        at: new Date().toISOString(),
        siteTz: r.site_tz ?? status.site_tz ?? null,
        photo: photoPath && shot ? shot.dataUrl : null,
      });
      setPending(r.pending_autocloses ?? []);
    } catch (e) {
      setRefusal(errorMessage(e, 'Try again.'));
    } finally {
      setBusy(null);
    }
  }

  async function onResolve(item: PendingAutoclose, resolution: AutocloseResolution, outAt: string, note: string): Promise<boolean> {
    setBusy('resolve'); setRefusal(null);
    try {
      const r = await resolveAutoclose(phone, pin, item, resolution, outAt, note);
      if (r.ok === false) { setRefusal(r.message ?? 'Try again.'); return false; }
      setPending((p) => p.filter((x) => x.exception_id !== item.exception_id));
      return true;
    } catch (e) {
      setRefusal(errorMessage(e, 'Try again.'));
      return false;
    } finally {
      setBusy(null);
    }
  }

  // Ben, 29 Oct: the company name is the biggest thing on the screen, so people who
  // work at the building but not for TCS can see at a glance this clock is not theirs.
  const header = (
    <div className="flex flex-col items-center gap-3">
      <div className="flex w-full justify-end gap-1">
        {(['es', 'en'] as const).map((l) => (
          <button key={l} type="button" onClick={() => switchLang(l)}
            className={`rounded-md px-2 py-0.5 text-xs font-semibold ${lang === l ? 'bg-tcs-inverse text-tcs-inverse-ink' : 'text-tcs-text-secondary'}`}>
            {l.toUpperCase()}
          </button>
        ))}
      </div>
      <div className={BRAND}>Terminal Cleaning Services</div>
    </div>
  );

  return (
    <div className="mx-auto flex min-h-full max-w-xl flex-col px-6 py-6" data-kiosk-device={status.device_id}>
      {header}

      {done ? (
        <div className="flex flex-1 flex-col items-center justify-center text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-st-ok-bg text-4xl font-bold text-st-ok-fg">✓</div>
          <div className="mt-3 text-3xl font-extrabold text-tcs-text">{done.firstName}</div>
          <div className="text-2xl font-bold text-st-ok-fg">{done.eventType === 'in' ? t.inRecorded : t.outRecorded}</div>
          <div className="mt-1 text-lg tabular-nums text-tcs-text">{formatSiteTime(done.at, done.siteTz)}</div>
          <div className="mt-1 text-sm text-tcs-text-secondary">{status.place_display}</div>
          {done.photo && <img src={done.photo} alt="" className="mt-3 h-20 w-20 rounded-xl object-cover" />}
          {!done.photo && photoMode === 'on' && <div className="mt-3 text-xs text-tcs-text-muted">{t.noCamera}</div>}
          <AutoclosePrompt t={t} pending={pending} busy={busy} onResolve={onResolve} />
          {refusal && <div className="mt-3 rounded-xl border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg">{refusal}</div>}
          {pending.length === 0 && (
            <div className="mt-4 text-xs text-tcs-text-muted">
              {t.clearsIn} · <button type="button" className="underline" onClick={clearAll}>{t.done}</button>
            </div>
          )}
        </div>
      ) : !ctx ? (
        <>
          <div className="mt-4 text-sm text-tcs-text-secondary">
            {t.clockHere}
            <div className="text-xl font-bold text-tcs-text">{status.place_display}</div>
          </div>
          <label className="mt-5 block text-xs font-semibold text-tcs-text-secondary" htmlFor="kiosk-phone">{t.phone}</label>
          <input id="kiosk-phone" value={phone} inputMode="tel" autoComplete="off"
            onChange={(e) => setPhone(e.target.value)}
            className="mt-1 w-full rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-3 text-xl text-tcs-text" />
          <label className="mt-3 block text-xs font-semibold text-tcs-text-secondary" htmlFor="kiosk-pin">{t.pin}</label>
          <input id="kiosk-pin" value={pin} inputMode="numeric" type="password" autoComplete="off"
            onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
            className="mt-1 w-44 rounded-lg border border-tcs-border-input bg-tcs-field px-3 py-3 text-center text-xl tracking-[0.4em] text-tcs-text" />
          <Button loading={busy === 'context'} disabled={phone.trim() === '' || pin.length < 4}
            onClick={() => void enter()} className="mt-5 w-full !h-14 !text-lg">
            {t.signIn}
          </Button>
          {refusal && <div className="mt-3 rounded-lg border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg">{refusal}</div>}
        </>
      ) : photoMode === 'notice' ? (
        <div className="mt-4" data-testid="kiosk-photo-notice">
          <div className="flex items-baseline justify-between">
            <h1 className="text-2xl font-bold text-tcs-text">{t.hello}, {ctx.first_name}</h1>
            <button type="button" onClick={clearAll} className="text-xs text-tcs-text-muted underline">{t.notYou}</button>
          </div>
          <div className="mt-3 rounded-2xl border border-tcs-border bg-tcs-surface p-4">
            <div className="text-xl font-bold text-tcs-text">{t.noticeTitle}</div>
            <p className="mt-2 text-base text-tcs-text">{t.noticeBody1}</p>
            <p className="mt-2 text-base text-tcs-text">{t.noticeBody2}</p>
          </div>
          <Button loading={busy === 'photo'} disabled={busy !== null} onClick={() => void choosePhoto('photo')}
            className="mt-4 w-full !h-16 !text-xl font-bold">
            {t.takePhoto}
          </Button>
          <Button variant="secondary" disabled={busy !== null} onClick={() => void choosePhoto('no_photo')}
            className="mt-3 w-full !h-14 !text-lg">
            {t.noPhoto}
          </Button>
          {refusal && <div className="mt-3 rounded-xl border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg">{refusal}</div>}
        </div>
      ) : (
        <>
          <div className="mt-4 flex items-baseline justify-between">
            <h1 className="text-2xl font-bold text-tcs-text">{t.hello}, {ctx.first_name}</h1>
            <button type="button" onClick={clearAll} className="text-xs text-tcs-text-muted underline">{t.notYou}</button>
          </div>
          <div className="text-sm text-tcs-text-secondary">{status.place_display}</div>
          {ctx.on_clock && (
            <div className="mt-2 rounded-xl border border-st-ok-border bg-st-ok-bg px-3 py-2 text-sm text-st-ok-fg">
              {t.onClockSince} {formatSiteTime(ctx.on_clock.since, ctx.on_clock.site_tz)} {t.at} {ctx.on_clock.place_display}
            </div>
          )}
          {photoMode === 'on' && (<>
          <div className="relative mt-3 overflow-hidden rounded-2xl bg-tcs-surface" style={{ aspectRatio: '4 / 3' }}>
            {camera !== 'failed' && (
              <video ref={videoRef} muted playsInline autoPlay className="h-full w-full object-cover" style={{ transform: 'scaleX(-1)' }} />
            )}
            <span className="absolute bottom-2 left-2 rounded-md bg-tcs-inverse px-2 py-0.5 text-[11px] text-tcs-inverse-ink">
              {camera === 'failed' ? '—' : t.lookCamera}
            </span>
          </div>
          {camera === 'failed' && (
            <div className="mt-2 rounded-xl border border-st-info-border bg-st-info-bg px-3 py-2 text-sm text-st-info-fg">{t.noCamera}</div>
          )}
          </>)}
          {photoMode === 'declined' && (
            <div className="mt-3 rounded-xl border border-tcs-border bg-tcs-surface px-3 py-3 text-sm text-tcs-text">{t.pinOnly}</div>
          )}
          <Button variant={ctx.on_clock ? 'secondary' : undefined} loading={busy === 'in' || busy === 'out'} disabled={busy !== null}
            onClick={() => void punch(ctx.on_clock ? 'out' : 'in')} className="mt-4 w-full !h-20 !text-2xl font-bold">
            {ctx.on_clock ? t.punchOut : t.punchIn}
          </Button>
          <div className="mt-2 text-[11px] text-tcs-text-muted">{t.recordedAlways}</div>
          {(photoMode === 'on' || photoMode === 'declined') && (
            <button type="button" disabled={busy !== null} data-testid="kiosk-photo-toggle"
              onClick={() => void choosePhoto(photoMode === 'on' ? 'no_photo' : 'photo')}
              className="mt-3 block w-full text-center text-sm text-tcs-accent underline">
              {photoMode === 'on' ? t.noPhotoFromNow : t.photosAgain}
            </button>
          )}
          {refusal && <div className="mt-3 rounded-xl border border-st-danger-border bg-st-danger-bg px-3 py-2 text-sm text-st-danger-fg">{refusal}</div>}
        </>
      )}

      <footer className="mt-auto pt-8 text-center text-xs text-tcs-text-muted">
        {HR_HELP_LINE ? `${t.help}: ${HR_HELP_LINE}` : t.helpNoNumber}
      </footer>
    </div>
  );
}
